// TakeawayCard.tsx
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { BillType, ORDER_TYPE } from '../../../all-types';
import { deliveryOptions } from '../../../constants';
import { CommonBtn } from '../../../components';
import { updateTakeawayBillStatus } from '../../../services';
import { FC, useEffect, useState } from 'react';
import { BillPrinter } from '../../printer';

const StatusChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.78rem',
  height: 28,
  fontWeight: 900,
  textTransform: 'capitalize',
  borderRadius: 8,
}));

const OrderTypeBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  fontSize: '0.75rem',
  fontWeight: 500,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottomLeftRadius: theme.shape.borderRadius,
  borderBottomRightRadius: theme.shape.borderRadius,
}));

const getTimeFromValue = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const formatElapsedTime = (startedAt?: number | null, now = Date.now()) => {
  if (!startedAt) return '-';

  const totalMinutes = Math.max(0, Math.floor((now - startedAt) / 60000));
  if (totalMinutes < 1) return 'Now';

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours} hr`;
  return `${hours} hr ${minutes} min`;
};

type TakeawayCardProps = {
  order: BillType;
  refersh?: () => void;
  viewOnly?: boolean;
};

const TakeawayCard: FC<TakeawayCardProps> = ({ order, refersh, viewOnly = false }) => {
  const theme = useTheme();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  const getTypeTextColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return theme.palette.freeTable?.main || theme.palette.success.main;
      case ORDER_TYPE.TAKEAWAY:
        return '#E7AA5A';
      default:
        return theme.palette.text.primary;
    }
  };

  const getTypeBorderColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return '#ADEED8';
      case ORDER_TYPE.TAKEAWAY:
        return '#FFE6BB';
      default:
        return theme.palette.divider;
    }
  };

  const getTypeColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return theme.palette.color8?.main || theme.palette.background.paper;
      case ORDER_TYPE.TAKEAWAY:
        return '#FFF9ED';
      default:
        return theme.palette.background.paper;
    }
  };

  const updateTakeawayOrderStatus = async (status = 'READY') => {
    try {
      if (order?._id) {
        await updateTakeawayBillStatus(order._id, status);
        refersh?.();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getInstructions = (inst?: string) => {
    if (!inst) return '';
    try {
      const instructions: { [key: string]: string } = JSON.parse(inst);
      return (
        Object.entries(instructions)
          ?.map(([key, value]) => `${key}: ${value}`)
          .join(', ') || ''
      );
    } catch (e) {
      console.error(e);
      return '';
    }
  };

  if (!order) return null;

  const orderStartedAt =
    getTimeFromValue(order?.timeInfo?.timestamp) || getTimeFromValue(order?.createdAt);
  const orderElapsedLabel = formatElapsedTime(orderStartedAt, now);
  const isReady = order?.takeAwayStatus === 'READY';
  const statusLabel = isReady ? 'Ready' : order?.takeAwayStatus?.toLowerCase() || 'pending';
  const cardAccentColor = isReady ? '#81C784' : '#FFCC80';
  const cardAccentTextColor = isReady ? '#2E7D32' : '#A15C00';
  const cardAccentBgColor = isReady ? '#F1F8E9' : '#FFF8E1';
  const paymentTextColor = order.modeOfPayment === 'CASH' ? '#8A5A00' : '#2E7D32';
  const paymentBorderColor = order.modeOfPayment === 'CASH' ? '#E6C36A' : '#81C784';

  return (
    <Grid item xs={12} sm={6} lg={4}>
      <Card
        elevation={4}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          cursor: 'pointer',
          borderRadius: 3,
          backgroundColor: cardAccentBgColor,
          border: `1px solid ${cardAccentColor}`,
          overflow: 'hidden',
          '&:hover': { boxShadow: 6 },
        }}
      >
        <Box sx={{ height: 5, bgcolor: cardAccentColor }} />
        <CardContent sx={{ flexGrow: 1 }}>
          <Stack justifyContent="space-between" flex={1} gap={1}>
            {/* Header row */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                <img
                  src={deliveryOptions[order?.takeAwayType || 'Take-Away/Parcel']?.logoUrl}
                  alt={order?.takeAwayType || 'Take-Away/Parcel'}
                  style={{ width: 42, height: 42, borderRadius: 10 }}
                />
                <Box minWidth={0}>
                  <Typography variant="h6" fontWeight={900} lineHeight={1.1} noWrap>
                    {order?.takeAwayType}
                  </Typography>
                  <Typography variant="body2" fontWeight={800} color="text.secondary">
                    Order #{order?.sNo}
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Box
                  sx={{
                    px: 1.75,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    color: cardAccentTextColor,
                    border: `1px solid ${cardAccentColor}`,
                    minWidth: 112,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" fontWeight={900} lineHeight={1.1} sx={{ display: 'block' }}>
                    Elapsed
                  </Typography>
                  <Typography variant="h6" fontWeight={900} lineHeight={1.2}>
                    {orderElapsedLabel}
                  </Typography>
                </Box>
                <StatusChip
                  label={statusLabel}
                  sx={{
                    bgcolor: 'background.paper',
                    color: cardAccentTextColor,
                    border: `1px solid ${cardAccentColor}`,
                  }}
                />
              </Box>
            </Stack>

            <Divider />

            {/* Items */}
            <Box sx={{ mt: 1, maxHeight: 280, pr: 0.5, overflowY: 'auto' }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Items:
              </Typography>

              {(order.items || []).map((item: any, index: number) => {
                const rowKey = item?._id || item?.itemId || `${order._id || order.billNo}-${index}`;
                return (
                  <Stack key={rowKey} gap={1} mt={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="body2" component="div">
                        {index + 1}• {item.itemName} (x {item.noOfItems})
                      </Typography>
                      <Typography variant="body2" fontWeight={800} color="text.secondary" component="div">
                        {`${item.quantity <= 1 ? '' : item.quantity} ${item.quantityType || ''}`.trim()}
                      </Typography>
                    </Stack>
                    {!!item?.kitchInfo?.kitchenInstrcutions && (
                      <Typography variant="body2" component="div" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {getInstructions(item?.kitchInfo?.kitchenInstrcutions)}
                      </Typography>
                    )}
                    <Divider />
                  </Stack>
                );
              })}
            </Box>

            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1 }}>
              Amount:{' '}
              <Box component="span" fontWeight="bold">
                ₹{order?.billingInfo?.finalBillAmount || 0}
              </Box>
            </Typography>
          </Stack>
        </CardContent>

        {!viewOnly && (
          <Stack alignItems="flex-end" p={2} gap={2}>
            {order?.takeAwayStatus !== 'DONE' &&
              (order?.takeAwayStatus === 'READY' ? (
                <>
                  <CommonBtn variant="contained" color="success" onClick={() => updateTakeawayOrderStatus('DONE')}>
                    Mark as Complete
                  </CommonBtn>
                  <BillPrinter bill={order} takeawayKitchenPrint />
                </>
              ) : (
                <>
                  <CommonBtn variant="contained" onClick={() => updateTakeawayOrderStatus('READY')}>
                    Mark as Ready
                  </CommonBtn>
                  {/* <CommonBtn variant='contained' color='info' onClick={() => updateTakeawayOrderStatus("READY")}>Print</CommonBtn> */}
                </>
              ))}
          </Stack>
        )}

        <OrderTypeBadge
          sx={{
            bgcolor: getTypeColor(order.orderType),
            color: getTypeTextColor(order.orderType),
            borderTop: `1px dashed ${getTypeBorderColor(order.orderType)}`,
          }}
        >
          <Typography variant="caption" textTransform="capitalize" fontWeight={700}>
            {order.orderType.toLowerCase()}
          </Typography>
          <StatusChip
            label={order.modeOfPayment === 'CASH' ? 'Cash' : 'Paid'}
            size="small"
            sx={{
              width: 'auto',
              bgcolor: 'background.paper',
              color: paymentTextColor,
              border: `1px solid ${paymentBorderColor}`,
              height: 24,
              fontSize: '0.72rem',
            }}
          />
        </OrderTypeBadge>
      </Card>
    </Grid>
  );
};

export { TakeawayCard };
